import React, { useState, useEffect } from 'react'
import { Search, Plus, Edit, Trash2, RefreshCw, ChevronDown, CheckCircle, XCircle, X, Eye, Clock, Filter } from 'lucide-react'
import hrShiftService from '../../services/hrShiftService'
import InlineLoader from '../../components/ui/InlineLoader'
import AddShiftModal from '../../components/modals/AddShiftModal'
import EditShiftModal from '../../components/modals/EditShiftModal'
import ViewShiftModal from '../../components/modals/ViewShiftModal'
import DeleteShiftModal from '../../components/modals/DeleteShiftModal'
import { format } from 'date-fns'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-2 ${
    type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
    <span>{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2">
      <X className="h-4 w-4" />
    </button>
  </div>
)

const ShiftManagement = () => {
  const [shifts, setShifts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedShift, setSelectedShift] = useState(null)
  const [toastMessage, setToastMessage] = useState(null)

  // Helper function to safely get property value
  const safeGet = (obj, path, defaultValue = '') => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToastMessage({ message, type })
    setTimeout(() => setToastMessage(null), 3000)
  }

  useEffect(() => {
    fetchShifts()
  }, [])

  const fetchShifts = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await hrShiftService.getAllShifts()
      if (response.success) {
        setShifts(response.data.shifts || [])
      } else {
        setError('Failed to fetch shifts')
      }
    } catch (error) {
      console.error('Error fetching shifts:', error)
      setError(error.message || 'Failed to load shifts')
      setShifts([])
    } finally {
      setLoading(false)
    }
  }

  const handleCreateShift = async (shiftData) => {
    try {
      const response = await hrShiftService.createShift(shiftData)
      if (response.success) {
        showToast('Shift created successfully')
        fetchShifts()
        setShowAddModal(false)
      } else {
        showToast(response.message || 'Failed to create shift', 'error')
      }
    } catch (error) {
      console.error('Error creating shift:', error)
      showToast(error.message || 'Failed to create shift', 'error')
    }
  }

  const handleEditShift = async (shiftData) => {
    try {
      const response = await hrShiftService.updateShift(selectedShift._id, shiftData)
      if (response.success) {
        showToast('Shift updated successfully')
        fetchShifts()
        setShowEditModal(false)
        setSelectedShift(null)
      } else {
        showToast(response.message || 'Failed to update shift', 'error')
      }
    } catch (error) {
      console.error('Error updating shift:', error)
      showToast(error.message || 'Failed to update shift', 'error')
    }
  }

  const handleDeleteShift = async () => {
    try {
      const response = await hrShiftService.deleteShift(selectedShift._id)
      if (response.success) {
        showToast('Shift deleted successfully')
        fetchShifts()
        setShowDeleteModal(false)
        setSelectedShift(null)
      } else {
        showToast(response.message || 'Failed to delete shift', 'error')
      }
    } catch (error) {
      console.error('Error deleting shift:', error)
      showToast(error.message || 'Failed to delete shift', 'error')
    }
  }

  // Filter shifts based on search and status
  const filteredShifts = shifts.filter(shift => {
    const matchesSearch = (
      safeGet(shift, 'shiftName', '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      safeGet(shift, 'shiftCode', '').toLowerCase().includes(searchTerm.toLowerCase())
    )

    const matchesStatus = statusFilter === 'all' || safeGet(shift, 'status') === statusFilter

    return matchesSearch && matchesStatus
  })

  // Format time for display
  const formatTime = (timeString) => {
    if (!timeString) return ''
    try {
      const [hours, minutes] = timeString.split(':')
      const date = new Date()
      date.setHours(parseInt(hours), parseInt(minutes))
      return format(date, 'h:mm a')
    } catch {
      return timeString
    }
  }

  // Calculate total hours
  const calculateTotalHours = (shift) => {
    const workingHours = safeGet(shift, 'workingHours', 0)
    const breakHours = safeGet(shift, 'breakHours', 0)
    return workingHours + breakHours
  }

  if (loading) {
    return (
      <InlineLoader text="Loading shifts..." />
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchShifts}
            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Shift Management</h1>
        <div className="flex items-center space-x-4">
          <button
            onClick={fetchShifts}
            className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Create Shift</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filter Shifts</h2>
          </div>
          {(searchTerm || statusFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('')
                setStatusFilter('all')
              }}
              className="px-4 py-2 text-orange-600 border border-orange-600 rounded-lg hover:bg-orange-50 transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search shift name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-3 w-full border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none pr-10 bg-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Results count */}
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredShifts.length} of {shifts.length} shifts
        </div>

        {/* Summary Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-100">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-6 w-6 text-orange-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Shifts</p>
                <p className="text-xl font-semibold text-gray-900">{shifts.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-100">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="h-6 w-6 text-green-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Active Shifts</p>
                <p className="text-xl font-semibold text-gray-900">
                  {shifts.filter(shift => shift.status === 'active').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-red-50 p-4 rounded-lg border border-red-100">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XCircle className="h-6 w-6 text-red-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Inactive Shifts</p>
                <p className="text-xl font-semibold text-gray-900">
                  {shifts.filter(shift => shift.status === 'inactive').length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Shifts Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">All Shifts</h3>
        </div>

        {filteredShifts.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No shifts found</p>
            <p className="text-sm text-gray-400">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria' 
                : 'Get started by creating your first shift'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shift Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Hours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Break
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Start Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    End Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredShifts.map((shift) => (
                  <tr key={shift._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {safeGet(shift, 'shiftName', 'N/A')}
                        </div>
                        <div className="text-sm text-gray-500">
                          Code: {safeGet(shift, 'shiftCode', 'N/A')}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {calculateTotalHours(shift)} Hours
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {safeGet(shift, 'breakHours', 0)} Hour{safeGet(shift, 'breakHours', 0) !== 1 ? 's' : ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatTime(safeGet(shift, 'startTime'))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatTime(safeGet(shift, 'endTime'))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        safeGet(shift, 'status') === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {safeGet(shift, 'status', 'Unknown').charAt(0).toUpperCase() + 
                         safeGet(shift, 'status', 'Unknown').slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => {
                          setSelectedShift(shift)
                          setShowViewModal(true)
                        }}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                        title="View Shift"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedShift(shift)
                          setShowEditModal(true)
                        }}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                        title="Edit Shift"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedShift(shift)
                          setShowDeleteModal(true)
                        }}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                        title="Delete Shift"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <AddShiftModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleCreateShift}
      />

      <EditShiftModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setSelectedShift(null)
        }}
        onSubmit={handleEditShift}
        shift={selectedShift}
      />

      <ViewShiftModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false)
          setSelectedShift(null)
        }}
        shift={selectedShift}
      />

      <DeleteShiftModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setSelectedShift(null)
        }}
        onConfirm={handleDeleteShift}
        shift={selectedShift}
      />

      {/* Toast Notification */}
      {toastMessage && (
        <Toast
          message={toastMessage.message}
          type={toastMessage.type}
          onClose={() => setToastMessage(null)}
        />
      )}
    </div>
  )
}

export default ShiftManagement
